import api from './client'
import type {
  AccountActivity,
  AccountAnchor,
  Appointment,
  AppointmentListQuery,
  AppointmentPayload,
  AppointmentSettings,
  AppointmentUpdatePayload,
} from '../api'

function anchorPath(anchor: AccountAnchor): string {
  return anchor.kind === 'merchant'
    ? `/merchants/${anchor.id}`
    : `/leads/${anchor.id}`
}

export const appointmentsApi = {
  settings: async (): Promise<AppointmentSettings> => {
    const response = await api.get<AppointmentSettings>('/appointments/settings')
    return response.data
  },

  listMine: async (query: AppointmentListQuery = {}): Promise<Appointment[]> => {
    const rows: Appointment[] = []
    let page = 1
    let lastPage = 1
    do {
      const response = await api.get<{ data: Appointment[]; last_page?: number }>('/appointments', {
        params: { ...query, page, per_page: 200 },
      })
      rows.push(...(response.data.data ?? []))
      lastPage = response.data.last_page ?? 1
      page += 1
      if (page > 500) {
        throw new Error('Appointment range is too large to load.')
      }
    } while (page <= lastPage)

    return rows
  },

  listForAnchor: async (anchor: AccountAnchor): Promise<Appointment[]> => {
    const response = await api.get<{ data: Appointment[] }>(`${anchorPath(anchor)}/appointments`)
    return response.data.data
  },

  create: async (anchor: AccountAnchor, payload: AppointmentPayload): Promise<Appointment> => {
    const response = await api.post<Appointment>(`${anchorPath(anchor)}/appointments`, payload)
    return response.data
  },

  update: async (appointmentId: string, payload: AppointmentUpdatePayload): Promise<Appointment> => {
    const response = await api.patch<Appointment>(`/appointments/${appointmentId}`, payload)
    return response.data
  },

  reschedule: async (appointmentId: string, startsAt: string): Promise<Appointment> => {
    const response = await api.post<Appointment>(`/appointments/${appointmentId}/reschedule`, {
      starts_at: startsAt,
    })
    return response.data
  },

  cancel: async (appointmentId: string): Promise<Appointment> => {
    const response = await api.post<Appointment>(`/appointments/${appointmentId}/cancel`)
    return response.data
  },

  complete: async (appointmentId: string): Promise<Appointment> => {
    const response = await api.post<Appointment>(`/appointments/${appointmentId}/complete`)
    return response.data
  },

  noShow: async (appointmentId: string): Promise<Appointment> => {
    const response = await api.post<Appointment>(`/appointments/${appointmentId}/no-show`)
    return response.data
  },

  activity: async (anchor: AccountAnchor): Promise<AccountActivity[]> => {
    const response = await api.get<{ data: AccountActivity[] }>(`${anchorPath(anchor)}/activity`)
    return response.data.data
  },
}
